import { useState, useEffect, useCallback } from 'react'

function safeJSONParse<T>(value: string | null, fallback: T): T {
  if (!value) return fallback
  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

/**
 * A custom React hook that provides reactive access to sessionStorage values.
 * Automatically syncs state across browser tabs and handles JSON serialization.
 *
 * @param key - The sessionStorage key to store/retrieve the value
 * @param initialValue - Default value if no value exists in sessionStorage
 * @returns [value, setValue, clearValue] - Current value, function to update it, function to clear it
 * @template T - Type of the stored value
 */
export function useSessionStorage<T>(key: string, initialValue: T) {
  // Get initial value from sessionStorage or use initialValue
  const [value, setValue] = useState<T>(() => {
    return safeJSONParse(sessionStorage.getItem(key), initialValue)
  })

  // Listen to storage events
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key && event.storageArea === sessionStorage) {
        setValue(event.newValue ? JSON.parse(event.newValue) : initialValue)
      }
    }

    // Add event listener
    window.addEventListener('storage', handleStorageChange)

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [key, initialValue])

  // Function to update storage
  const updateValue = useCallback(
    (newValue: T) => {
      setValue(newValue)
      sessionStorage.setItem(key, JSON.stringify(newValue))
      // Dispatch custom event to notify other components
      window.dispatchEvent(
        new StorageEvent('storage', {
          key,
          newValue: JSON.stringify(newValue),
          storageArea: sessionStorage,
        })
      )
    },
    [key]
  )

  const clearValue = useCallback(() => {
    setValue(initialValue)
    sessionStorage.removeItem(key)
    window.dispatchEvent(
      new StorageEvent('storage', {
        key,
        newValue: null,
        storageArea: sessionStorage,
      })
    )
  }, [key, initialValue])

  return [value, updateValue, clearValue] as const
}
